/**
 * The gradebook data seam.
 *
 * Every read and write the four Gradebook pages perform goes through this file
 * and nowhere else. It talks to the Postgres-backed `/api/gradebooks` routes -
 * the contract lives in `easyreports-backend/src/docs/gradebook-api.md`.
 *
 * WHY THE MUTATORS ARE A HOOK, not the plain exported functions they used to be:
 * every request needs a Clerk JWT, and the only way to reach one is
 * `useAuth().getToken()` - a hook, unreachable from a module-level function. The
 * alternative (registering a token getter into a module variable once at mount)
 * would have kept the old call sites, but it would also bypass
 * `useApiFetch`, and with it the `api-error:429/403/500` events that
 * `ApiErrorModal` (mounted in root-layout) listens for. Routing through
 * `useApiFetch` is what makes a rate-limited or failed gradebook request tell
 * the user something instead of failing silently.
 *
 * Reads are hooks that own their own loading/error state and expose `refetch`.
 * Mutations return promises and do NOT refetch on their own: the sheet keeps
 * optimistic local state and must never wait on a server to show the number the
 * teacher just typed. Callers refetch when they want the canonical copy back.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "@/utils/apiFetch";

/**
 * `useApiFetch()` returns a fresh closure every render, so using it directly in
 * an effect's dependency array re-runs that effect forever. Holding it in a ref
 * and handing back one stable wrapper is what lets the read hooks below depend
 * on it honestly instead of silencing the lint rule.
 */
function useStableApiFetch() {
  const apiFetch = useApiFetch();
  const ref = useRef(apiFetch);
  ref.current = apiFetch;
  return useCallback((path, options) => ref.current(path, options), []);
}

async function request(apiFetch, method, path, body) {
  const res = await apiFetch(path, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });

  if (!res.ok) {
    // Surface the server's own message (limits, validation) when there is one;
    // apiFetch has already fired the global modal event for 429/403/5xx.
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body - keep the generic message */
    }
    throw Object.assign(new Error(message), { status: res.status });
  }

  return res.status === 204 ? null : res.json();
}

/* ── reads ──────────────────────────────────────────────────────────────── */

/** Every gradebook the signed-in teacher owns. `GET /api/gradebooks`. */
export function useGradebooks() {
  const apiFetch = useStableApiFetch();
  const [state, setState] = useState({ gradebooks: [], loading: true, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const gradebooks = await request(apiFetch, "GET", "/api/gradebooks");
      setState({ gradebooks, loading: false, error: null });
    } catch (error) {
      setState({ gradebooks: [], loading: false, error });
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refetch: load };
}

/**
 * One gradebook with its roster, columns, categories and marks.
 * `GET /api/gradebooks/:id` - one payload, the whole dataset for the sheet.
 */
export function useGradebook(gradebookId) {
  const apiFetch = useStableApiFetch();
  const [state, setState] = useState({ gradebook: null, loading: true, error: null });

  /* Initial load, and a genuine reload when the id changes - the only two times
   * a spinner is correct. Clearing to null first matters: without it, switching
   * gradebooks would briefly render the previous book's marks under the new
   * book's heading. */
  useEffect(() => {
    if (!gradebookId) return undefined;
    let cancelled = false;
    setState({ gradebook: null, loading: true, error: null });

    request(apiFetch, "GET", `/api/gradebooks/${gradebookId}`)
      .then((gradebook) => {
        if (!cancelled) setState({ gradebook, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ gradebook: null, loading: false, error });
      });

    return () => { cancelled = true; };
  }, [apiFetch, gradebookId]);

  /**
   * Re-fetch WITHOUT flipping `loading`.
   *
   * The sheet is keyed on `gradebook.id`, so anything that shows the spinner
   * unmounts and remounts it - losing scroll position, cursor and optimistic
   * marks, and reading as the whole page blinking. A revalidation must leave the
   * current data on screen and swap it underneath.
   *
   * A failed revalidation keeps the data already on screen rather than replacing
   * a working sheet with an error; `useApiFetch` has already raised the global
   * modal for 429/403/5xx, so the failure is not silent.
   */
  const refetch = useCallback(async () => {
    if (!gradebookId) return;
    try {
      const gradebook = await request(apiFetch, "GET", `/api/gradebooks/${gradebookId}`);
      setState({ gradebook, loading: false, error: null });
    } catch {
      /* keep showing what we have */
    }
  }, [apiFetch, gradebookId]);

  /**
   * Patch the cached gradebook in place.
   *
   * This is what lets a new student or column appear the instant the server
   * confirms it, with no second round-trip and no re-render of anything else.
   * Pass a pure updater: `mutate(g => ({ ...g, students: [...g.students, s] }))`.
   */
  const mutate = useCallback((updater) => {
    setState((s) => (s.gradebook ? { ...s, gradebook: updater(s.gradebook) } : s));
  }, []);

  return { ...state, refetch, mutate };
}

/* ── writes ─────────────────────────────────────────────────────────────── */

/**
 * The gradebook mutators, bound to the signed-in teacher.
 *
 *   const api = useGradebookApi();
 *   await api.saveMark(gradebookId, studentId, assessmentId, mark);
 *
 * Every one returns a promise. Callers that hold optimistic state should attach
 * a `.catch()` - an unhandled rejection here would be a silent lost edit.
 */
export function useGradebookApi() {
  const apiFetch = useStableApiFetch();

  return useMemo(() => {
    const send = (method, path, body) => request(apiFetch, method, path, body);
    const gb = (id) => `/api/gradebooks/${id}`;

    return {
      /** `POST /api/gradebooks` - the backend seeds its categories in the same transaction. */
      createGradebook: (values) => send("POST", "/api/gradebooks", values),
      /** Subject, grade, school year, title colour. */
      updateGradebook: (id, changes) => send("PUT", gb(id), changes),
      /** Soft delete - cascades to columns and marks. */
      deleteGradebook: (id) => send("DELETE", gb(id)),

      createStudent: (id, student) => send("POST", `${gb(id)}/students`, student),
      updateStudent: (id, studentId, changes) =>
        send("PUT", `${gb(id)}/students/${studentId}`, changes),
      deleteStudent: (id, studentId) => send("DELETE", `${gb(id)}/students/${studentId}`),

      createAssessment: (id, assessment) => send("POST", `${gb(id)}/assessments`, assessment),
      updateAssessment: (id, assessmentId, changes) =>
        send("PUT", `${gb(id)}/assessments/${assessmentId}`, changes),
      deleteAssessment: (id, assessmentId) =>
        send("DELETE", `${gb(id)}/assessments/${assessmentId}`),

      /**
       * Full replace of the gradebook's category set - the weights modal edits
       * them as a unit. Categories dropped here leave their assessments alone;
       * those fall back to "Not counted".
       */
      saveCategories: (id, categories) => send("PUT", `${gb(id)}/categories`, { categories }),

      /**
       * One cell. A single UPSERT on UNIQUE(assessment, student) server-side.
       * The caller should debounce (~500ms) - this fires per committed edit.
       *
       * SENDS THE WHOLE CELL, ALWAYS - both on a single mark and on a K/T/C/A
       * split, where the marks live in `scoreK/T/C/A` instead of `score`.
       *
       * This is what makes the sync queue safe, and it is worth spelling out.
       * The queue keeps only the LAST callback per key so that typing `100`
       * sends one request rather than three. The key is the CELL
       * (`studentId:assessmentId`), and one cell is one database row - so if a
       * write carried only the section the teacher just edited, typing K and
       * then T would let T's queued write replace K's and the K mark would be
       * dropped before it was ever sent. Because every write carries all four
       * sections from the sheet's own state, last-write-wins loses nothing.
       *
       * The backend upsert REPLACES the row rather than merging into it, which
       * is the other half of the same reasoning: a partial payload there would
       * null the sections it omitted.
       *
       * Undefined is normalised to null so an untouched section is explicitly
       * cleared rather than silently left behind. There is no `percent` - it was
       * a second stored form of the same number and the column is gone
       * (backend migration `drop_entry_percent`).
       */
      saveMark: (id, studentId, assessmentId, mark) =>
        send("PUT", `${gb(id)}/assessments/${assessmentId}/entries/${studentId}`, {
          status: mark.status,
          score: mark.score ?? null,
          scoreK: mark.scoreK ?? null,
          scoreT: mark.scoreT ?? null,
          scoreC: mark.scoreC ?? null,
          scoreA: mark.scoreA ?? null,
        }),

      /**
       * Clear a cell back to never-marked. Distinct from a status: `missing`
       * counts as a zero and `excused` drops out of the average, but both record
       * a teacher's decision. An emptied cell records none, which is the absence
       * of a row - hence a delete, not a status write.
       */
      clearMark: (id, studentId, assessmentId) =>
        send("DELETE", `${gb(id)}/assessments/${assessmentId}/entries/${studentId}`),
    };
  }, [apiFetch]);
}
